import BigNumber from 'bignumber.js';
import numeral from 'numeral';

import { kSeparator } from './price';

export const checkEvenNumber = (integer: number) => integer % 2 === 0;

export const checkIsNumber = (value: number) => !isNaN(value);

export const checkPositiveNumber = (integer: number) => integer > 0;

export const formatAccountBalance = (balance: number) => {
  const initialFormattedBalance = kSeparator(balance, 2);

  if (Math.sign(balance) < 0) {
    const formattedPL = initialFormattedBalance.replace('-', '-$');
    return `${formattedPL}`;
  }

  return `$${initialFormattedBalance}`;
};

export const formatAccountListingBalance = (balance: number, baseCurrency: string) => {
  const initialFormattedBalance = kSeparator(balance, 2);

  if (Math.sign(balance) < 0) {
    return `- ${initialFormattedBalance} ${baseCurrency}`;
  }

  return `${initialFormattedBalance} ${baseCurrency}`;
};

export const formatPercentagePNL = (pnl: string) => {
  let formattedPnl = pnl.toString();
  if (checkPositiveNumber(parseFloat(pnl))) {
    formattedPnl = '+' + formattedPnl;
  }
  return `${formattedPnl}%`;
};

export const formatProfitLoss = (pnl: number) => {
  const initialFormattedPL = kSeparator(pnl, 2);

  if (Math.sign(pnl) < 0) {
    const formattedPL = initialFormattedPL.replace('-', '-$');
    return `${formattedPL}`;
  }

  if (Math.sign(pnl) > 0) {
    return `+$${initialFormattedPL}`;
  }

  return `$${initialFormattedPL}`;
};

export const calculateDecimalPlaces = (value: number | string) => {
  const stringValue = typeof value === 'number' ? value.toString() : value;

  return stringValue.includes('.') ? stringValue.split('.')[1].length : 0;
};

export const formatDecimal = (value: number | string | undefined, decimal = 2, formatter = '') => {
  if (typeof value === 'string' && isNaN(Number(value))) {
    throw `${value} is not a number!`;
  }

  if (value === undefined) {
    return value;
  }

  const format = formatter || `0,0.${Array(decimal).fill('0').join('')}`;

  return numeral(value).format(format);
};

export const numberOrUndefined = (value: unknown) => {
  return Number.isFinite(value) ? (value as number) : undefined;
};

export const numberOrDefault = (value: unknown, defaultValue = 0) => {
  return Number.isFinite(value) ? (value as number) : defaultValue;
};

export const numberAsStringOrUndefined = (value: unknown) => {
  return Number.isFinite(value) ? String(value) : undefined;
};

export const numberAsBigNumberOrUndefined = (value: unknown) => {
  return Number.isFinite(value) ? new BigNumber(value as number) : undefined;
};

export const numberForDisplay = (value: unknown, decimal?: number | null) => {
  return Number.isFinite(value) ? new BigNumber(value as number).toFormat(numberOrUndefined(decimal)) : '--';
};

export const bigNumberForDisplay = (value?: BigNumber, decimal?: number | null) => {
  return value?.toFormat(numberOrUndefined(decimal)) || '--';
};

const PARSE_NUMBER_STRING_REGEX = /^\d*(?:\.(\d+))?/;

export const getNumberStringAndDecimalsFromValue = (
  value: string | number | undefined | null
): [string, number] | undefined => {
  const match = String(value).match(PARSE_NUMBER_STRING_REGEX);
  return match ? [match[0], match[1]?.length || 0] : undefined;
};

export const getDecimalsFromValue = (value: string | number | undefined | null): number | undefined => {
  return String(value).match(PARSE_NUMBER_STRING_REGEX)?.[1]?.length || undefined;
};

interface IFormatToDecimalsParams {
  value?: number;
  decimals?: number;
}

export const formatToDecimals = (params: IFormatToDecimalsParams) => {
  const { value, decimals } = params;
  return new BigNumber(numberOrDefault(value)).toFormat(decimals);
};

/** Function to add thousandSeperator to number string and maintain it decimal value
 *
 * 1000.000 => 1,000.000
 * 2000.100 => 2,000.10
 *
 */
export const formatNumberWithThousandSeparator = (input: string): string => {
  // If the input already contains a thousands separator, return it as is
  if (!input || input.includes(',')) {
    return input;
  }
  const [integerPart, fractionalPart] = input.split('.');
  const formattedIntegerPart = parseInt(integerPart, 10).toLocaleString();
  return fractionalPart ? `${formattedIntegerPart}.${fractionalPart}` : formattedIntegerPart;
};
